import { useImageFallback } from '@susu/custom-made/hooks/useImageFallback';
import { useMissingValues } from '@susu/custom-made/hooks/useMissingValues';
import { FALLBACK_IMAGE_CLASSES, FALLBACK_IMAGE_CONTAINER_CLASSES } from '@susu/custom-made/utils/fallbackImage';
import { imageDefinition } from '@susu/custom-made/utils/imageDefinition';
import { imageUrlBuilder } from '@susu/custom-made/utils/imageUrlProvider/imageUrlProvider';
import type {
  Monogram,
  MonogramItem,
  Option,
  OptionConfigurationType,
  typeOptionValue,
} from '@susu/custom-made/utils/interfaces';
import type {
  MissingMonogramValuesPerConfig,
  MissingOptions,
  MissingOptionsPerConfiguration,
  MissingOptionValues,
  MissingOptionValuesPerConfiguration,
} from '@susu/custom-made/utils/preloadConfiguration/preloadConfiguration';
import { getOptionById } from '@susu/custom-made/utils/preloadConfiguration/preloadConfiguration';
import type { ConfigurationTypes } from '@susu/custom-made/utils/productDefinition';
import { getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId } from '@susu/custom-made/utils/productDefinition';
import { getSectionByConfigurationType } from '@susu/custom-made/utils/navigation';
import { MISSING_OPTION_DETAILS_TEST_IDS } from '@susu/custom-made/utils/testIds';
import {
  getConfigurationTypeKey,
  getImagePaths,
  getImageUrl,
  isAdditonalProduct,
  isObjectEmpty,
} from '@susu/custom-made/utils/utils';
import cn from 'classnames';
import React, { useMemo } from 'react';

import './missingOptionsDetails.css';
import { useEnvironmentContext } from '@susu/custom-made/providers/hooks/useEnvironmentContext';
import { useI18N } from '@susu/i18n/client';
import useFabricContext from '@susu/custom-made/providers/hooks/useFabricContext';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import useOptionContext from '@susu/custom-made/providers/hooks/useOptionContext';

type MissingOptionsDetailsProps = {
  options: OptionConfigurationType;
  optionValues: typeOptionValue;
  missingOptionValuesPerConfig: MissingOptionValuesPerConfiguration;
  missingMonogramValuesPerConfig: MissingMonogramValuesPerConfig;
  missingOptionsPerConfig: MissingOptionsPerConfiguration;
  isSummaryValidationFlow: boolean;
};

type FormattedOptionValue = {
  optionName: string;
  optionId: number;
  optionValueName: string;
  optionThumbnail: string;
  optionFallbackThumbnail: string;
};

type FormattedOption = {
  optionName: string;
  optionId: number;
  optionThumbnail?: string;
  optionFallbackThumbnail?: string;
};

type FormattedMissingMonogramValue = {
  monogramId: number;
  monogramPosition: string | null;
  missingMonogramText: string;
  monogramThumbnail: string;
  isPositionMissing: boolean;
};

const MissingOptionsDetails = ({
  options,
  optionValues,
  missingOptionValuesPerConfig,
  missingMonogramValuesPerConfig,
  missingOptionsPerConfig,
  isSummaryValidationFlow,
}: MissingOptionsDetailsProps) => {
  const { t } = useI18N();
  const { isAdditionalProductAdded } = useOptionContext();
  const { missingValueClickHandler, removeSelectedMonogramFromMissingValues } = useMissingValues();
  const { genericFallbackImages, handleImageError, handleImageLoad } = useImageFallback();
  const { title, description } = useMemo(() => {
    const flowKey = isSummaryValidationFlow ? 'summaryValidationFlow' : 'preloadFlow';
    return {
      title: t(`missingOptions.${flowKey}.title`),
      description: t(`missingOptions.${flowKey}.description`),
    };
  }, [isSummaryValidationFlow, t]);
  const { selectedFabric } = useFabricContext();
  const { product } = useUserContext();
  const { cdnDomain } = useEnvironmentContext();

  const getFormattedMissingOptionValuesData = (
    optionValues: MissingOptionValues,
    options: Option[],
  ): FormattedOptionValue[] => {
    return Object.values(optionValues).reduce<FormattedOptionValue[]>((acc, optionValue) => {
      const matchingOption = getOptionById(optionValue.optionId, options);

      if (matchingOption) {
        const { imageThumbnail, fallbackImageThumbnail } = getImagePaths(
          matchingOption.imageUrlTemplate,
          optionValue.code,
          selectedFabric?.layerImageName || '',
          cdnDomain,
        );

        acc.push({
          optionName: matchingOption.name,
          optionId: optionValue.optionId,
          optionValueName: optionValue.name,
          optionThumbnail: imageThumbnail,
          optionFallbackThumbnail: fallbackImageThumbnail,
        });
      }
      return acc;
    }, []);
  };


  const MISSING_OPTION_IMAGE_SIZE_CLASSES = 'w-64 h-64 md:w-80 md:h-80 2xl:w-[100px] 2xl:h-[100px]';

  const getMissingOptionImageContainerClasses = (isGenericFallback: boolean) =>
    cn('block rounded-md', MISSING_OPTION_IMAGE_SIZE_CLASSES, {
      [FALLBACK_IMAGE_CONTAINER_CLASSES]: isGenericFallback,
    });

  const getMissingOptionImageClasses = (isGenericFallback: boolean) =>
    cn('block rounded-md', MISSING_OPTION_IMAGE_SIZE_CLASSES, {
      'object-cover': !isGenericFallback,
      [FALLBACK_IMAGE_CLASSES]: isGenericFallback,
    });

  const renderMissingOptionValues = () => {
    if (!missingOptionValuesPerConfig) {
      return;
    }

    return Object.entries(missingOptionValuesPerConfig)
      .sort(([configurationTypeId1], [configurationTypeId2]) =>
        configurationTypeId2.localeCompare(configurationTypeId1),
      )
      .reduce<JSX.Element[]>((acc, [configurationTypeId, missingOptionValues]) => {
        if (
          isAdditonalProduct(product!, configurationTypeId as ConfigurationTypes) &&
          !isAdditionalProductAdded[configurationTypeId]
        ) {
          return acc;
        }
        if (!isObjectEmpty(missingOptionValues)) {
          const associatedSection = getSectionByConfigurationType(configurationTypeId);
          const associatedSectionPresentationalName = t(`configurationType.${getConfigurationTypeKey(configurationTypeId as ConfigurationTypes)}`);

          // To handle extra configurations which have different configuration type id but using definitions from base product
          const baseConfigurationTypeId =
            getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeId);
          const configurationOptions = options[baseConfigurationTypeId].items;
          const formattedMissingValues = getFormattedMissingOptionValuesData(missingOptionValues, configurationOptions);

          const sectionElement = (
            <div
              className="flex flex-col justify-start w-full px-16 pb-32 md:px-20"
              key={`${configurationTypeId}-${associatedSection}`}
            >
              <div className="text-left text-sm leading-16 font-medium pb-16 md:text-base md:leading-base">{associatedSectionPresentationalName}</div>
              <div className="flex flex-col gap-12 md:gap-16">
                {formattedMissingValues.map(missingValue => (
                  <div
                    key={`${missingValue.optionName}-${missingValue.optionValueName}`}
                    className="flex flex-row gap-12 hover:cursor-pointer md:gap-20"
                    data-option-id={missingValue.optionId}
                    data-associated-section={associatedSection}
                    onClick={e => {
                      missingValueClickHandler(e, configurationOptions);
                    }}
                  >
                    <div
                      className={getMissingOptionImageContainerClasses(
                        !!genericFallbackImages[missingValue.optionThumbnail],
                      )}
                    >
                      <img
                        src={missingValue.optionThumbnail}
                        className={getMissingOptionImageClasses(
                          !!genericFallbackImages[missingValue.optionThumbnail],
                        )}
                        alt="missing-option-img"
                        onError={e => {
                          handleImageError(e, missingValue.optionThumbnail, missingValue.optionFallbackThumbnail);
                        }}
                        onLoad={e => {
                          handleImageLoad(e, missingValue.optionThumbnail);
                        }}
                      />
                    </div>
                    <div className="flex flex-col justify-center items-start text-left w-full text-sm leading-sm md:text-base md:leading-base">
                      <div className="font-medium">{missingValue.optionName}</div>
                      <div>{missingValue.optionValueName}</div>
                    </div>
                    <div className="flex justify-center items-center mr-16 2xl:mr-20 text-gray-600 text-xl">
                      <i className="susu-icons" translate="no">
                        arrow_right
                      </i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );

          acc.push(sectionElement);
        }
        return acc;
      }, []);
  };

  const generateMissingMonogramDetails = (
    missingMonogramValue: MonogramItem,
    monogramOption: Option,
    monogramPositionId: number,
  ) => {
    const isPositionMissing: boolean =
      Object.keys(missingMonogramValue).length === 1 && Object.keys(missingMonogramValue).includes('positionName');
    const affectedMonogramPosition =
      missingMonogramValue.positionName ?? monogramOption.positions![+monogramPositionId]?.name;
    const missingMonogramValuesKeys: string[] = [];

    Object.keys(missingMonogramValue).forEach(monogramValueKey => {
      if (monogramValueKey === 'colorId') {
        const missingColor = missingMonogramValue['colorName']
          ? t('missingOptions.colorLabel', { color: missingMonogramValue['colorName'] })
          : t('missingOptions.color');
        missingMonogramValuesKeys.push(missingColor);
      }

      if (monogramValueKey === 'fontStyleId') {
        const missingFont = missingMonogramValue['fontName']
          ? t('missingOptions.fontLabel', { font: missingMonogramValue['fontName'] })
          : t('missingOptions.font');
        missingMonogramValuesKeys.push(missingFont);
      }
    });

    const missingMonogramText =
      missingMonogramValuesKeys.length > 0 ? missingMonogramValuesKeys.toString() : t('missingOptions.position');

    return {
      missingMonogramText: missingMonogramText,
      affectedMonogramPosition: affectedMonogramPosition,
      isPositionMissing: isPositionMissing,
    };
  };

  const getFormattedMissingMonogramValuesData = (
    monogramValues: Monogram,
    options: Option[],
  ): FormattedMissingMonogramValue[] => {
    return Object.entries(monogramValues).reduce<FormattedMissingMonogramValue[]>(
      (acc, [monogramPositionId, monogramValue]) => {
        const monogramOption = options.find(option => option.type === 'MONOGRAM');
        if (!monogramOption) {
          return acc;
        }

        const monogramThumbnail = imageUrlBuilder({
          imageUrls: [
            getImageUrl(
              monogramOption.imageUrlTemplate,
              monogramOption.imageName,
              selectedFabric?.layerImageName || '',
            ).imageUrl,
          ],
          imageDefinition: imageDefinition.thumbnail,
          noTransformation: true,
          cdnDomain,
        }).src;

        const { missingMonogramText, affectedMonogramPosition, isPositionMissing } = generateMissingMonogramDetails(
          monogramValue,
          monogramOption,
          +monogramPositionId,
        );

        acc.push({
          monogramId: +monogramPositionId,
          monogramPosition: affectedMonogramPosition,
          missingMonogramText,
          monogramThumbnail,
          isPositionMissing: isPositionMissing,
        });

        return acc;
      },
      [],
    );
  };

  const renderMissingMonogramValues = () => {
    if (!missingMonogramValuesPerConfig) {
      return;
    }

    return Object.entries(missingMonogramValuesPerConfig)
      .sort(([configurationTypeId1], [configurationTypeId2]) =>
        configurationTypeId2.localeCompare(configurationTypeId1),
      )
      .reduce<JSX.Element[]>((acc, [configurationTypeId, missingMonogramValues]) => {
        const associatedSection = getSectionByConfigurationType(configurationTypeId);
        const associatedSectionPresentationalName = t(`configurationType.${getConfigurationTypeKey(configurationTypeId as ConfigurationTypes)}`);
        // To handle extra configurations which have different configuration type id but using definitions from base product
        const baseConfigurationTypeId =
          getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeId);
        const configurationOptions = options[baseConfigurationTypeId].items;
        const formattedMonogramValues = getFormattedMissingMonogramValuesData(
          missingMonogramValues,
          configurationOptions,
        );

        const sectionElement = (
          <div className="flex flex-col justify-start w-full px-16 pb-32 md:px-20" key={'missing-monograms'}>
            <div className="text-left text-sm leading-16 font-medium pb-16 md:text-base md:leading-base">{t('missingOptions.monograms')}</div>
            <div className="flex flex-col gap-12 md:gap-16">
              {formattedMonogramValues.map(missingValue => (
                <div
                  key={`${missingValue.monogramId}`}
                  className="flex flex-row gap-12 hover:cursor-pointer md:gap-20"
                  data-position-id={missingValue.monogramId}
                  data-associated-section={associatedSection}
                  onClick={e => {
                    missingValueClickHandler(e, configurationOptions);
                  }}
                >
                  <img src={missingValue.monogramThumbnail} className="block w-64 h-64 rounded-md object-cover md:w-80 md:h-80 2xl:w-[100px] 2xl:h-[100px]" alt="missing-monogram-img" />
                  <div className="flex flex-col justify-center items-start text-left w-full text-sm leading-sm md:text-base md:leading-base">
                    <div className="font-medium">
                      {missingValue.monogramPosition
                        ? missingValue.monogramPosition
                        : associatedSectionPresentationalName}
                    </div>
                    <div>{missingValue.missingMonogramText}</div>
                    {missingValue.isPositionMissing && <div className="text-red-600 text-xs">{t('missingOptions.naRemove')}</div>}
                  </div>
                  {missingValue.isPositionMissing && (
                    <div
                      className="flex justify-center items-center mr-16 2xl:mr-20 text-gray-600 text-xl"
                      data-testid={MISSING_OPTION_DETAILS_TEST_IDS.REMOVE_MONOGRAM_BTN}
                      onClick={e => {
                        e.stopPropagation();
                        removeSelectedMonogramFromMissingValues(configurationTypeId, missingValue.monogramId);
                      }}
                    >
                      <i className="susu-icons" translate="no">
                        trash
                      </i>
                    </div>
                  )}

                  <div className="flex justify-center items-center mr-16 2xl:mr-20 text-gray-600 text-xl">
                    <i className="susu-icons" translate="no">
                      arrow_right
                    </i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

        acc.push(sectionElement);
        return acc;
      }, []);
  };

  const getFormattedOptionsWithMissingSelectionData = (
    missingOptions: MissingOptions,
    options: Option[],
  ): FormattedOption[] => {
    return Object.values(missingOptions).reduce<FormattedOption[]>((acc, missingOption) => {
      const matchingOption = getOptionById(missingOption.id, options);

      if (matchingOption) {
        const firstOptionValueOfMissingOption = optionValues[matchingOption.optionValues![0]];

        const formattedMissingOption: FormattedOption = {
          optionName: matchingOption.name,
          optionId: matchingOption.id,
        };

        if (firstOptionValueOfMissingOption) {
          const { imageThumbnail, fallbackImageThumbnail } = getImagePaths(
            matchingOption.imageUrlTemplate,
            firstOptionValueOfMissingOption.code,
            selectedFabric?.layerImageName || '',
            cdnDomain,
          );

          formattedMissingOption.optionThumbnail = imageThumbnail;
          formattedMissingOption.optionFallbackThumbnail = fallbackImageThumbnail;
        }

        acc.push(formattedMissingOption);
      }

      return acc;
    }, []);
  };

  const renderOptionsWithMissingSelection = () => {
    if (!missingOptionsPerConfig || isObjectEmpty(missingOptionsPerConfig)) {
      return;
    }

    return Object.entries(missingOptionsPerConfig)
      .sort(([configurationTypeId1], [configurationTypeId2]) =>
        configurationTypeId2.localeCompare(configurationTypeId1),
      )
      .reduce<JSX.Element[]>((acc, [configurationTypeId, missingOptions]) => {
        if (
          isAdditonalProduct(product!, configurationTypeId as ConfigurationTypes) &&
          !isAdditionalProductAdded[configurationTypeId]
        ) {
          return acc;
        }

        if (!isObjectEmpty(missingOptions)) {
          const associatedSection = getSectionByConfigurationType(configurationTypeId);
          const associatedSectionPresentationalName = t(`configurationType.${getConfigurationTypeKey(configurationTypeId as ConfigurationTypes)}`);

          // To handle extra configurations which have different configuration type id but using definitions from base product
          const baseConfigurationTypeId =
            getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationTypeId);
          const configurationOptions = options[baseConfigurationTypeId].items;
          const formattedMissingValues = getFormattedOptionsWithMissingSelectionData(
            missingOptions,
            configurationOptions,
          );

          const sectionElement = (
            <div
              className="flex flex-col justify-start w-full px-16 pb-32 md:px-20"
              key={`${configurationTypeId}-${associatedSection}`}
            >
              <div className="text-left text-sm leading-16 font-medium pb-16 md:text-base md:leading-base">{associatedSectionPresentationalName}</div>
              <div className="flex flex-col gap-12 md:gap-16">
                {formattedMissingValues.map(missingValue => (
                  <div
                    key={`${missingValue.optionName}`}
                    className="flex flex-row gap-12 hover:cursor-pointer md:gap-20"
                    data-option-id={missingValue.optionId}
                    data-associated-section={associatedSection}
                    onClick={e => {
                      missingValueClickHandler(e, configurationOptions);
                    }}
                  >
                    <div
                      className={getMissingOptionImageContainerClasses(
                        !!genericFallbackImages[missingValue.optionThumbnail ?? ''],
                      )}
                    >
                      <img
                        src={missingValue.optionThumbnail}
                        className={getMissingOptionImageClasses(
                          !!genericFallbackImages[missingValue.optionThumbnail ?? ''],
                        )}
                        alt="missing-option-img"
                        onError={e => {
                          handleImageError(e, missingValue.optionThumbnail ?? '', missingValue.optionFallbackThumbnail);
                        }}
                        onLoad={e => {
                          handleImageLoad(e, missingValue.optionThumbnail ?? '');
                        }}
                      />
                    </div>
                    <div className="flex flex-col justify-center items-start text-left w-full text-sm leading-sm md:text-base md:leading-base">
                      <div className="font-medium">{missingValue.optionName}</div>
                    </div>
                    <div className="flex justify-center items-center mr-16 2xl:mr-20 text-gray-600 text-xl">
                      <i className="susu-icons" translate="no">
                        arrow_right
                      </i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );

          acc.push(sectionElement);
        }
        return acc;
      }, []);
  };

  return (
    <div className="flex flex-col justify-center items-center text-center">
      <div className="flex flex-col justify-center items-center max-w-[263px] mt-12 mb-32 md:mt-64 md:mx-24">
        <div className="cmMissingAlertIcon bg-contain border border-solid border-red-600 rounded-full flex items-center justify-center w-28 h-28 md:w-36 md:h-36" />
        <div className="text-lg font-medium leading-base pt-16 pb-8 md:text-xl md:leading-lg">{title}</div>
        <div className="text-sm leading-sm">{description}</div>
      </div>
      {renderMissingOptionValues()}
      {isSummaryValidationFlow && renderOptionsWithMissingSelection()}
      {renderMissingMonogramValues()}
    </div>
  );
};

export default React.memo(MissingOptionsDetails);
